<template>
	<div>
		<div v-if="isLoading" class="spinner-container">
			<Spinner />
		</div>
		<div v-else>
			<!-- todo: if no payoffs display none found -->
			<div class="table-wrapper">
				<table>
					<thead>
						<tr>
							<th
								v-for="header in headers"
								:key="header.key"
								:class="isActiveSort(header.key)"
								@click="sortBy(header.key)"
							>
								<span class="header-label">
									{{ header.label }}
									<span v-if="header.sortable">
										{{ getSortArrow(header.key) }}
									</span>
								</span>
							</th>
						</tr>
					</thead>
					<tbody>
						<PayoffRow
							v-for="payoff in sortedPayoffs"
							:key="payoff.id"
							:payoff="payoff"
							@payoffUpdatedAsync="refreshDataAsync"
						/>
					</tbody>
				</table>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { onMounted, ref, computed } from 'vue';
	import { useRoute, useRouter } from 'vue-router';
	import axios from 'axios';
	import functions from '@/use/functions';
	import Spinner from '@/components/shared/Spinner.vue';
	import PayoffRow from '@/components/loan/refi/PayoffRow.vue';
	import { PayoffDetails } from '@/models/loans';

	const props = defineProps<{
		loanId: number;
		lenderId: number;
		onPayoffsChanged: () => Promise<void>;
	}>();

	const route = useRoute();
	const router = useRouter();

	const isLoading = ref<boolean>(true);
	const payoffs = ref<PayoffDetails[]>([]);

	const headers = ref([
		{ label: 'Disb Id', sortable: true, key: 'id' },
		{ label: 'Batch Id', sortable: true, key: 'batchId' },
		{ label: 'Status', sortable: true, key: 'disbursementStatusHumanized' },
		{ label: 'Lender', sortable: true, key: 'lenderName' },
		{ label: 'Servicer', sortable: true, key: 'servicerName' },
		{ label: 'Account #', sortable: true, key: 'accountNumber' },
		{ label: 'Sent Date', sortable: true, key: 'transactionDateFormatted' },
		{ label: 'Original Disb', sortable: true, key: 'disbursementAmountAmountFormatted' },
		{ label: 'Total', sortable: true, key: 'adjustedDisbursementAmountAmountFormatted' },
		{ label: 'Payment Method', sortable: true, key: 'disbursementTypeHumanized' },
		{ label: 'Settle Date', sortable: true, key: 'settleDateFormatted' }
	]);

	const sortKey = ref('');
	const sortOrder = ref(1);

	const sortedPayoffs = computed(() => {
		if (!sortKey.value) {
			return payoffs.value; // no sorting, return original order
		}

		return [...payoffs.value].sort((a, b) => {
			let valA = a ? a[sortKey.value] : null;
			let valB = b ? b[sortKey.value] : null;

			if (typeof valA === 'string') {
				valA = valA.toLowerCase();
			}

			if (typeof valB === 'string') {
				valB = valB.toLowerCase();
			}

			if (valA < valB) {
				return -1 * sortOrder.value;
			} else if (valA > valB) {
				return 1 * sortOrder.value;
			}

			return 0;
		});
	});

	const sortBy = (key: string) => {
		if (sortKey.value === key) {
			if (sortOrder.value === 1) {
				sortOrder.value = -1; // second click, descending
			} else if (sortOrder.value === -1) {
				sortKey.value = ''; // third click, reset sort
				sortOrder.value = 1; // default sort order for the next click
			}
		} else {
			sortKey.value = key;
			sortOrder.value = 1; // first click, ascending
		}

		router.push({
			query: {
				...route.query,
				payoffSortKey: sortKey.value,
				payoffSortOrder: sortOrder.value.toString()
			},
			hash: route.hash
		});
	};

	const getSortArrow = (key: string) => {
		if (sortKey.value === key) {
			return sortOrder.value === 1 ? '▲' : sortOrder.value === -1 ? '▼' : ' ';
		}

		return '';
	};

	const isActiveSort = (key: string) => (sortKey.value === key ? 'active-sort' : '');

	async function refreshDataAsync() {
		await getPayoffsAsync();
		await props.onPayoffsChanged();
	}

	async function getPayoffsAsync() {
		try {
			const response = await axios(`/api/loanpayoffs/loan/${props.loanId}/lender/${props.lenderId}`);

			payoffs.value = response.data;
		} catch (error) {
			functions.openModal({
				title: 'Oops!',
				description: 'There was an issue getting the draws. Please try again later.'
			});
		}
	}

	function intializeSorting() {
		const querySortKey = route.query.payoffSortKey as string;
		const querySortOrder = parseInt(route.query.payoffSortOrder as string, 10);

		if (querySortKey) {
			sortKey.value = querySortKey;
		}

		if (!isNaN(querySortOrder) && (querySortOrder === 1 || querySortOrder === -1)) {
			sortOrder.value = querySortOrder;
		}
	}

	onMounted(async () => {
		intializeSorting();

		await getPayoffsAsync();

		isLoading.value = false;
	});
</script>

<style scoped>
	.spinner-container {
		display: flex;
		justify-content: center;
		margin-top: 4rem;
	}

	.table-wrapper {
		overflow-x: auto;
		width: 100%;
	}

	table {
		width: 100%;
		border-collapse: collapse;
		font-size: 14px;
		border: 1px solid #ccc;
	}

	thead th {
		background-color: #f2f2f2;
		font-weight: 800;
		padding: 10px 20px;
		border-bottom: 1px solid #ccc;
		cursor: pointer;
		user-select: none;
	}

	.header-label {
		white-space: nowrap;
		line-height: 1;
	}

	th:hover {
		background-color: #ddd;
	}

	tr:nth-child(even) {
		background-color: #f9f9f9;
	}

	th {
		text-align: center;
	}

	td:nth-child(1) {
		width: 40px;
	}
</style>
